import { DESCRIPTION_COPY_LIMITS, TITLE_COPY_LIMITS } from "../copy-policy";
/**
 * Highlights — a titled grid of short facts, the "At a Glance" band. Data
 * contract + slot schema.
 *
 * ONE block-level field, a title naming what the facts are about, and then a
 * repeat group the Editor sizes: there is no fixed number of facts. Everything
 * else belongs to a highlight.
 *
 * A highlight carries exactly two parts — a LABEL naming what the fact is about,
 * and a DESCRIPTION giving the fact itself. Both are plain words. Neither is the
 * structured document {@link richTextSchema} and {@link accordionSchema} store,
 * and neither can carry emphasis, links, headings or lists — a card draws its
 * description as a single paragraph, so a document would have nowhere to put its
 * formatting. An Editor who needs formatted copy reaches for Rich Text.
 *
 * NO MEDIA SLOTS ANYWHERE, on the band or on a highlight, and that is the
 * component rather than a gap in authoring: the design is a grid of type, and a
 * card has no place to draw a picture. This is the first block in the catalog to
 * declare `mediaSlots: []` at BOTH levels, which is also why it is the first
 * whose story reaches for none of `pick-image-for-slot`,
 * `upload-image-to-slot`, `position-fixed-crop` or `place-image-in-block-slot`.
 *
 * No call to action either. A band states facts and leads nowhere; a page that
 * needs to lead somewhere from beside them reaches for {@link promoSchema} or
 * {@link promoImageSchema}.
 *
 * The title RENDERS AS A HEADING and belongs to the page's outline — a band of
 * facts is a passage of a page. But it never OPENS one, so it is an `<h2>`,
 * never an `<h1>`. Same position {@link statementSchema} takes, and the exact
 * inverse of {@link calloutSchema}, whose statement stays body copy precisely so
 * that it never claims a place in the outline.
 *
 * The rules live in `../highlights`.
 */
import { HIGHLIGHTS_ITEMS_KEY, HIGHLIGHTS_MINIMUM_ITEMS } from "../highlights";
/**
 * The sizes this band's title may be set at, and the one it defaults to.
 *
 * Read twice from this one declaration: by the slot below, and by the renderer
 * in `@diality/ui`, so the menu cannot offer a step the renderer has no size for.
 *
 * DEFAULTS TO L (80px) THOUGH THE BAND SHIPPED AT 64, the same deliberate step
 * up {@link FEATURED_PRESS_TITLE_SCALE} takes: design asked for the larger
 * opening knowing it differs from the prototype. Adopting this field therefore
 * grows every published band's title one step; 64 remains reachable as M.
 */
export const HIGHLIGHTS_TITLE_SCALE = {
    default: "l",
    steps: ["xl", "l", "m"],
    sizes: "title",
};
export const highlightsSchema = {
    componentId: "Highlights",
    scalarSlots: [
        { key: "title", ...TITLE_COPY_LIMITS, label: "Title", type: "text" },
        {
            key: "titleSize",
            label: "Title size",
            type: "type-scale",
            scale: HIGHLIGHTS_TITLE_SCALE,
        },
    ],
    mediaSlots: [],
    repeatSlots: [
        {
            key: HIGHLIGHTS_ITEMS_KEY,
            label: "Highlights",
            // Singular: an Editor adds one Highlight at a time, even though the block
            // itself is named for the band.
            itemLabel: "Highlight",
            // A UI hint only, and the same number the validator enforces for real.
            minItems: HIGHLIGHTS_MINIMUM_ITEMS,
            scalarSlots: [
                { key: "label", label: "Label", type: "text" },
                {
                    key: "description",
                    ...DESCRIPTION_COPY_LIMITS,
                    label: "Description",
                    type: "textarea",
                },
            ],
            mediaSlots: [],
        },
    ],
};
//# sourceMappingURL=highlights.js.map